/**
 * Centralized React Query key factory for the user's paid subscriptions.
 *
 * Every list query — the per-comedian `useUserPaidSubscriptions` here in
 * @punchup/data AND the params-keyed `useUserPaidSubscriptions` in apps/web
 * (used by LibraryWrapper) — keys under the shared `all` prefix. Because React
 * Query invalidation is prefix-matched, a mutation can invalidate `all` once
 * and refresh every consumer regardless of its param shape. This is what keeps
 * a future consumer from silently missing invalidation: there is no per-key
 * list to keep in sync.
 *
 * Flat tuples with `as const` for literal-type inference on `queryKey` /
 * `predicate` callers (matches crossPostingQueryKeys).
 */
export const paidSubscriptionKeys = {
  // Shared root prefix for every paid-subscription list query. Invalidate this
  // to refresh all of them at once.
  all: ["user", "paid-subscriptions"] as const,

  // Active subscriptions for a single comedian.
  byComedian: (comedianId: string | null | undefined) =>
    ["user", "paid-subscriptions", comedianId] as const,

  // Subscriptions keyed by arbitrary filter params (e.g. { active: true }).
  list: (params?: { active?: boolean }) =>
    ["user", "paid-subscriptions", params] as const,

  // Shared root prefix for the per-comedian max-tier query.
  tiersAll: ["user", "paid-subscription-tiers"] as const,

  tiers: (userId: string | null | undefined) =>
    ["user", "paid-subscription-tiers", userId] as const,
} as const;
